import type { ValueHistoryPeriod } from '@/schemas/portfolio'

/**
 * Query key factory for the portfolio domain.
 *
 * ── Everything about a book is SCOPED BY PROFILE ─────────────────────────────
 *
 * A member may hold several shareholder profiles and switch between them, and
 * every read below answers differently per profile. So `book(shareholderId)` is
 * a SCOPE rather than a query — the same pattern as `brokerKeys.broker(code)` —
 * and every per-profile read hangs off it. Without that scope, switching
 * profiles serves the previous profile's holdings for a frame, which is not a
 * flicker: it is one member's portfolio displayed under another's name.
 *
 * `undefined` means "whichever profile the backend resolves as primary", which
 * is a real and distinct scope: the API accepts the omitted parameter and
 * answers with `primary_or_first`. It is keyed as the literal `'primary'` so a
 * key never contains `undefined`, and so the URL and the key say the same
 * thing. Passing the primary profile's own id therefore caches the same rows a
 * second time under a second key — correct, just not shared, and the price of
 * never conflating "the primary, whichever that is" with "profile 7".
 */
export const portfolioKeys = {
  all: ['portfolio'] as const,

  /**
   * The member's profiles. NOT under `book(...)` — it is the list of scopes,
   * not something inside one, and it is the same answer whichever profile is
   * being viewed.
   */
  shareholders: () => [...portfolioKeys.all, 'shareholders'] as const,

  /**
   * Scope, not a query. Invalidate this to drop EXACTLY the nine reads below
   * for one profile, which is what every recorded transaction requires:
   *
   *   holdings · summary · overview · transactions · profitLoss
   *   analytics · income · health · valueHistory
   *
   * One buy moves the first five. `holdings` gains or grows a row; `summary`
   * and `overview` recompute their totals, WACC and weights; `transactions`
   * gains a ledger row; and `profitLoss` re-derives realised gain from the
   * historical WACC at each sale, which a new buy changes. A write that
   * refreshed only the ledger would leave a stale value on screen next to a
   * fresh row — the exact bug this scope exists to prevent.
   *
   * It moves the next three too, which is why they hang off the same scope:
   * `analytics` re-ranks its movers and re-solves the XIRR from a changed
   * cash-flow series, `health` re-derives every concentration check from the
   * new weights, and `income` re-reads which companies are still HELD — a buy
   * can add an upcoming book closure and a full exit removes one.
   *
   * `valueHistory` is the one member of this scope a write does NOT move: it
   * reads `portfolio_daily_snapshot`, which only a management command writes.
   * It sits here anyway because the alternative — a scope that covers eight of
   * nine reads — is a rule every future read has to be checked against, and
   * refetching an unchanged list costs one request. Over-invalidating shows the
   * member the same numbers; under-invalidating shows them last week's.
   */
  book: (shareholderId?: number) =>
    [...portfolioKeys.all, 'book', shareholderId ?? 'primary'] as const,

  /** Open positions. A list; biggest first, unpriced last. */
  holdings: (shareholderId?: number) => [...portfolioKeys.book(shareholderId), 'holdings'] as const,

  /**
   * Portfolio-level totals on their own.
   *
   * A separate branch from `overview` even though `overview` contains a
   * summary, because they are different response shapes and one must never be
   * able to answer for the other. A screen needing both halves should read
   * `overview` — one request, one consistent snapshot — rather than these two.
   */
  summary: (shareholderId?: number) => [...portfolioKeys.book(shareholderId), 'summary'] as const,

  /** Summary AND holdings from one call. What the Home card reads. */
  overview: (shareholderId?: number) => [...portfolioKeys.book(shareholderId), 'overview'] as const,

  /** The whole ledger: four arrays, newest first, unpaginated. */
  transactions: (shareholderId?: number) =>
    [...portfolioKeys.book(shareholderId), 'transactions'] as const,

  /** Realised and unrealised, open positions and closed ones. */
  profitLoss: (shareholderId?: number) =>
    [...portfolioKeys.book(shareholderId), 'profit-loss'] as const,

  /**
   * The value curve. The ONE read in this domain with a parameter of its own,
   * so the period is part of the key.
   *
   * Two periods are two different answers over two different windows — and not
   * just a longer slice of the same numbers: `drawdown_percent` is computed
   * relative to the peak WITHIN the window, so the identical calendar day
   * reports a different drawdown under `1m` than under `all`. Sharing one key
   * across periods would serve `1y`'s drawdowns under a `1m` label.
   */
  valueHistory: (period: ValueHistoryPeriod, shareholderId?: number) =>
    [...portfolioKeys.book(shareholderId), 'value-history', period] as const,

  /** Return measures, sector contribution, top five movers. */
  analytics: (shareholderId?: number) =>
    [...portfolioKeys.book(shareholderId), 'analytics'] as const,

  /** Six traffic-light checks and their three counters. There is no score. */
  health: (shareholderId?: number) => [...portfolioKeys.book(shareholderId), 'health'] as const,

  /** Dividends received per company, plus announced corporate actions. */
  income: (shareholderId?: number) => [...portfolioKeys.book(shareholderId), 'income'] as const,
}
